import { Link } from "react-router-dom";

import { AccountBar } from "@/components/AccountBar";
import { PlayerAvatar } from "@/components/PlayerAvatar";
import { pieceColorVar } from "@/game/pieces";
import { type PlayerState } from "@/lib/playersApi";
import { useAccount } from "@/lib/useAccount";

/**
 * Account management, on the hub.
 *
 * Signing in used to live only on the puzzle page, which made it something you
 * discovered rather than something you were offered. It belongs here: this is
 * the page that lists everything an account is *for*.
 */
export function HubAccount({
  player,
  error = null,
}: {
  readonly player: PlayerState | null;
  /** A failed load, so it is never mistaken for "this member is unassigned". */
  readonly error?: Error | null;
}) {
  const { session, loading, signOut } = useAccount();

  if (loading) {
    return (
      <div className="slab px-6 py-5">
        <p className="meta opacity-60">…</p>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="slab px-6 py-5">
        {/* No heading of its own — the band above already says "Your account",
            and repeating it read as a stutter. */}
        <p className="mb-5 max-w-[52ch] leading-relaxed opacity-75">
          An account lets you rate puzzles and, once an officer links you to
          your roster entry, write your own player profile. It is free and takes
          a moment.
        </p>
        <AccountBar prompt="Already have one?" />
      </div>
    );
  }

  const profile = player?.profile ?? null;
  const tint = profile?.favourite_piece
    ? pieceColorVar(profile.favourite_piece)
    : "var(--color-ink)";

  return (
    <div className="slab px-6 py-5" style={{ ["--slab-ink" as string]: tint }}>
      <div className="flex flex-wrap items-start gap-5">
        <PlayerAvatar
          username={profile?.roster_username ?? ""}
          hasAvatar={Boolean(profile?.has_avatar)}
          piece={profile?.favourite_piece ?? null}
          size="h-16 w-16"
          cell={10}
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h3 className="text-2xl normal-case">{session.username}</h3>
            {session.is_officer ? (
              <span className="meta border-2 border-ink bg-piece-t px-1.5 py-0.5 text-[0.625rem]">
                Officer
              </span>
            ) : null}
          </div>

          <p className="mt-2 max-w-[52ch] leading-relaxed opacity-75">
            {/* The unassigned sentence is a claim about this member, so it must
                not stand in for "we could not reach the server" — that told a
                linked member an officer had never linked them. */}
            {error
              ? "We could not load your player profile just now. Try again shortly."
              : player?.assigned
                ? `You write the profile for ${profile?.roster_username}.`
                : "Your account is not linked to a roster player yet. An officer has to make that link before you can write a profile."}
          </p>

          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/profile" className="btn px-4 py-2 text-sm">
              Your account &amp; ratings
            </Link>
            {player?.assigned && profile ? (
              <Link
                to={`/players/${encodeURIComponent(profile.roster_username)}`}
                className="btn btn--ghost px-4 py-2 text-sm"
              >
                Your player page
              </Link>
            ) : null}
            {session.is_officer ? (
              <Link to="/admin" className="btn btn--ghost px-4 py-2 text-sm">
                Club admin
              </Link>
            ) : null}
            <button
              type="button"
              className="btn btn--ghost px-4 py-2 text-sm"
              onClick={signOut}
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
